import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Drawer,
  Tabs,
  Tab,
  Divider,
  Grid,
  TextField
} from '@mui/material';
import {
  ExpandMore as ExpandMoreIcon,
  Refresh as RefreshIcon,
  Home as HomeIcon,
  CalendarToday as CalendarIcon,
  AccountBalance as LoanIcon,
  AccountBalanceWallet as AccountIcon,
  Business as BusinessIcon,
  Person as PersonIcon,
  Groups as FamilyIcon,
  AttachMoney as IncomeIcon,
  AccountBalanceWallet as BalancesIcon,
  Receipt as ReceiptIcon,
  Receipt as TransactionIcon,
  TrendingUp as TrendingUpIcon,
  Delete as DeleteIcon,
  DataObject as VisibilityIcon,
  Close as CloseIcon,
  Backup as BackupIcon
} from '@mui/icons-material';
import { api } from '../contexts/ApiContext';
import { effectiveMerchantName } from '../utils/effectiveMerchant';
import {
  FIT_TABLE_SX,
  FIT_TABLE_CONTAINER_SX,
  FitColgroup,
  fitTruncateSx,
} from '../utils/fitTable';

/** Entity tables here are text-heavy, so every body cell ellipsis-clips to its column. */
const DB_FIT_TABLE_SX = {
  ...FIT_TABLE_SX,
  '& .MuiTableBody-root .MuiTableCell-root:not(.fit-table-no-clip)': {
    whiteSpace: 'nowrap' as const,
    '& > *': {
      maxWidth: '100%',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap' as const,
    },
  },
};

interface Property {
  id: number;
  address: string;
  nickname: string;
  valuation: number;
  rental_income_yearly: number;
  lender: string;
  omar_ownership: number;
  heidi_ownership: number;
  sean_ownership: number;
  dwayne_ownership: number;
  lena_ownership: number;
  secodwom_uk_ltd_ownership: number;
  omhe_props_ltd_ownership: number;
  airbnb_ical_url?: string;
  vrbo_ical_url?: string;
  loans?: Loan[];
  created_at: string;
  updated_at: string;
}

interface Loan {
  id: number;
  loan_name: string;
  lender: string;
  original_amount: number;
  current_balance: number;
  interest_rate: number;
  monthly_payment: number;
  loan_type: string;
  start_date: string;
  end_date: string;
  property?: {
    id: number;
    nickname: string;
    address: string;
    valuation: number;
  };
}

interface Booking {
  id: number;
  property_id: number;
  property_nickname: string;
  platform: string;
  source: string;
  external_booking_id: string;
  guest_name?: string;
  guest_email?: string;
  check_in_date: string;
  check_out_date: string;
  nights: number;
  adults_count: number;
  children_count: number;
  total_payout_amount?: number;
  host_payout_amount?: number;
  currency: string;
  status: string;
  booking_type: string;
  summary?: string;
  description?: string;
  notes?: string;
  last_sync?: string;
  created_at: string;
  updated_at: string;
}

interface Company {
  id: number;
  name: string;
  nickname?: string;
  company_type: string;
  country?: string;
  registration_number?: string;
  tax_id?: string;
  address?: string;
  omar_ownership: number;
  heidi_ownership: number;
  dwayne_ownership: number;
  sean_ownership: number;
  lena_ownership: number;
  business_description?: string;
  website?: string;
  contact_email?: string;
  contact_phone?: string;
  is_active: boolean;
  created_at: string;
  updated_at: string;
}

interface Transaction {
  id: number;
  user_id: number;
  /** Ledger/settled date from API (`RevolutTransactionComplete.to_dict`) */
  display_date?: string;
  completed_at?: string;
  /** Legacy client mistake — API does not emit `date`; use `display_date` */
  date?: string;
  description?: string;
  reference?: string;
  category?: string;
  merchant_name?: string;
  merchant_category?: string;
  counterparty_name?: string;
  bank_account_id?: number;
  amount: number;
  currency: string;
  balance?: number;
  import_batch_id?: string;
  revolut_id?: string;
  /** Sidecar metadata from ``tx_appended_data`` (included on GET complete transactions). */
  derived_metadata?: Record<string, unknown>;
  created_at: string;
  updated_at: string;
}

const DatabaseOverview: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [lastRefresh, setLastRefresh] = useState<Date>(new Date());
  
  // Data state
  const [properties, setProperties] = useState<Property[]>([]);
  const [loans, setLoans] = useState<Loan[]>([]);
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [people, setPeople] = useState<any[]>([]);
  const [families, setFamilies] = useState<any[]>([]);
  const [incomes, setIncomes] = useState<any[]>([]);
  const [balances, setBalances] = useState<any[]>([]);
  const [accounts, setAccounts] = useState<any[]>([]);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [csvReferenceData, setCsvReferenceData] = useState<any>(null);
  const [databaseInfo, setDatabaseInfo] = useState<any>(null);
  const [databaseSchema, setDatabaseSchema] = useState<any>(null);
  const [databaseSchemaError, setDatabaseSchemaError] = useState<string | null>(null);
  const [databaseSchemaLoading, setDatabaseSchemaLoading] = useState(false);
  const [schemaTab, setSchemaTab] = useState(() => {
    const view = new URLSearchParams(window.location.search).get('view');
    if (view === 'erd') return 2;
    if (view === 'tables') return 1;
    return 0;
  });
  const [backupFiles, setBackupFiles] = useState<any[]>([]);
  const [backupMeta, setBackupMeta] = useState<any>(null);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [backupsError, setBackupsError] = useState<string | null>(null);
  const [erdSvg, setErdSvg] = useState('');
  const [erdSource, setErdSource] = useState('');
  const [erdError, setErdError] = useState('');
  const [deletingTransactions, setDeletingTransactions] = useState(false);
  const [deletingWebhooks, setDeletingWebhooks] = useState(false);
  
  // Additional tables state
  const [users, setUsers] = useState<any[]>([]);
  const [hostExpenses, setHostExpenses] = useState<any[]>([]);
  const [regularTransactions, setRegularTransactions] = useState<any[]>([]);
  const [revolutExpenses, setRevolutExpenses] = useState<any[]>([]);
  const [revolutCounterparties, setRevolutCounterparties] = useState<any[]>([]);
  const [webhooks, setWebhooks] = useState<any[]>([]);
  const [userEntityPermissions, setUserEntityPermissions] = useState<any[]>([]);
  const [revolutSyncMetadata, setRevolutSyncMetadata] = useState<any[]>([]);
  const [airbnbListings, setAirbnbListings] = useState<any[]>([]);
  const [airbnbBookings, setAirbnbBookings] = useState<any[]>([]);
  const [vrboListings, setVrboListings] = useState<any[]>([]);
  const [vrboBookings, setVrboBookings] = useState<any[]>([]);
  
  // Modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [selectedEntity, setSelectedEntity] = useState<any>(null);
  const [selectedEntityType, setSelectedEntityType] = useState<string>('');
  const [qvPanelOpen, setQvPanelOpen] = useState(false);
  const [txCommentInternal, setTxCommentInternal] = useState('');
  const [txCommentExternal, setTxCommentExternal] = useState('');
  const [txCommentsSaving, setTxCommentsSaving] = useState(false);
  const [txCommentsError, setTxCommentsError] = useState<string | null>(null);

  const fetchBackups = React.useCallback(async () => {
    setBackupsLoading(true);
    setBackupsError(null);
    try {
      const res = await api.get(`/api/admin/database-backups?_t=${Date.now()}`);
      setBackupFiles(res.data?.files || []);
      setBackupMeta(res.data || null);
    } catch (err: any) {
      const status = err?.response?.status;
      const backendMessage = err?.response?.data?.error;
      const reason =
        status === 403
          ? 'Backup list access denied (admin/owner required by API).'
          : backendMessage || 'Failed to fetch backup files.';
      setBackupsError(reason);
      setBackupFiles([]);
      setBackupMeta(null);
    } finally {
      setBackupsLoading(false);
    }
  }, []);

  const fetchAllData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Add cache-busting timestamp to ensure fresh data (no caching)
      const cacheBuster = `_t=${Date.now()}`;
      const addCacheBuster = (url: string) => {
        return url.includes('?') ? `${url}&${cacheBuster}` : `${url}?${cacheBuster}`;
      };

      // Fetch database schema first (to show latest structure)
      setDatabaseSchemaLoading(true);
      setDatabaseSchemaError(null);
      try {
        const schemaRes = await api.get(addCacheBuster('/api/admin/database-schema'));
        setDatabaseSchema(schemaRes.data);
        console.log('[DB OVERVIEW] Schema fetched:', schemaRes.data.tables?.length || 0, 'tables');
      } catch (err: any) {
        const status = err?.response?.status;
        const backendMessage = err?.response?.data?.error;
        const reason =
          status === 403
            ? 'Schema access denied (admin/owner required by API).'
            : backendMessage || 'Failed to fetch schema metadata.';
        setDatabaseSchemaError(reason);
        console.warn('[DB OVERVIEW] Failed to fetch schema:', err);
      } finally {
        setDatabaseSchemaLoading(false);
      }

      // Fetch essential data first (fast endpoints) - these load quickly
      const essentialData = await Promise.all([
        api.get(addCacheBuster('/api/properties/')),
        api.get(addCacheBuster('/api/loans/')),
        api.get(addCacheBuster('/api/calendar/unified/bookings')),
        api.get(addCacheBuster('/api/companies/')),
        api.get(addCacheBuster('/api/people/')),
        api.get(addCacheBuster('/api/families/')),
        api.get(addCacheBuster('/api/income/')),
        api.get(addCacheBuster('/api/balances/')),
        api.get(addCacheBuster('/api/accounts/')),
        api.get(addCacheBuster('/api/admin/users')).catch(() => ({ data: { users: [] } })),
        api.get(addCacheBuster('/api/host-expenses')).catch(() => ({ data: { expenses: [] } })),
        api.get(addCacheBuster('/api/transactions')).catch(() => ({ data: { transactions: [] } })),
        api.get(addCacheBuster('/api/calendar/airbnb/listings')).catch(() => ({ data: { listings: [] } })),
        api.get(addCacheBuster('/api/calendar/airbnb/bookings')).catch(() => ({ data: { bookings: [] } })),
        api.get(addCacheBuster('/api/calendar/vrbo/listings')).catch(() => ({ data: { listings: [] } })),
        api.get(addCacheBuster('/api/calendar/vrbo/bookings')).catch(() => ({ data: { bookings: [] } }))
      ]);

      // Set essential data immediately so page is usable
      setProperties(essentialData[0].data.properties || []);
      setLoans(essentialData[1].data.loans || []);
      setBookings(essentialData[2].data.bookings || []);
      setCompanies(essentialData[3].data.companies || []);
      setPeople(essentialData[4].data.people || []);
      setFamilies(essentialData[5].data.families || []);
      setIncomes(essentialData[6].data.incomes || []);
      setBalances(essentialData[7].data.balances || []);
      setAccounts(essentialData[8].data.accounts || []);
      setUsers(essentialData[9].data.users || []);
      setHostExpenses(essentialData[10].data.expenses || []);
      setRegularTransactions(essentialData[11].data.transactions || []);
      setAirbnbListings(essentialData[12].data.listings || []);
      setAirbnbBookings(essentialData[13].data.bookings || []);
      setVrboListings(essentialData[14].data.listings || []);
      setVrboBookings(essentialData[15].data.bookings || []);
      
      // Mark essential data as loaded
      setLoading(false);
      setLastRefresh(new Date());
      void fetchBackups();

      // Fetch ALL heavy data separately (no pagination limits) - fetch 100% of data
      Promise.all([
        api.get(addCacheBuster('/api/revolut/transactions/complete?all=true')).catch(err => {
          console.warn('[DB OVERVIEW] Failed to fetch all transactions:', err);
          return { data: { transactions: [] } };
        }),
        api.get(addCacheBuster('/api/admin/csv-reference-transactions')).catch(err => {
          console.warn('[DB OVERVIEW] Failed to fetch CSV reference data:', err);
          return { data: null };
        }),
        api.get(addCacheBuster('/api/revolut/expenses?all=true')).catch(() => ({ data: { expenses: [] } })),
        api.get(addCacheBuster('/api/revolut/counterparties?all=true')).catch(() => ({ data: { counterparties: [] } })),
        api.get(addCacheBuster('/api/admin/webhooks')).catch(() => ({ data: { webhooks: [] } }))
      ]).then(([transactionsRes, csvRefRes, expensesRes, counterpartiesRes, webhooksRes]) => {
        setTransactions(transactionsRes.data.transactions || []);
        setCsvReferenceData(csvRefRes.data);
        setRevolutExpenses(expensesRes.data.expenses || []);
        setRevolutCounterparties(counterpartiesRes.data.counterparties || []);
        // Webhooks endpoint returns { webhooks: [...], pagination: {...} }
        setWebhooks(webhooksRes.data.webhooks || []);
        console.log('[DB OVERVIEW] All data fetched - Transactions:', transactionsRes.data.transactions?.length || 0, 
                   'Expenses:', expensesRes.data.expenses?.length || 0,
                   'Counterparties:', counterpartiesRes.data.counterparties?.length || 0);
      });

      // Fetch entity permissions separately (multiple endpoints)
      Promise.all([
        api.get(addCacheBuster('/api/admin/entity-permissions/property')).catch(() => ({ data: { permissions: [] } })),
        api.get(addCacheBuster('/api/admin/entity-permissions/loan')).catch(() => ({ data: { permissions: [] } })),
        api.get(addCacheBuster('/api/admin/entity-permissions/company')).catch(() => ({ data: { permissions: [] } })),
        api.get(addCacheBuster('/api/admin/entity-permissions/income')).catch(() => ({ data: { permissions: [] } }))
      ]).then(results => {
        // Combine all permissions
        const allPermissions: any[] = [];
        results.forEach(result => {
          if (result.data && result.data.permissions) {
            allPermissions.push(...result.data.permissions);
          }
        });
        setUserEntityPermissions(allPermissions);
      }).catch(() => setUserEntityPermissions([]));

      // Revolut sync rows (no /api/admin/sync-metadata — that path does not exist; avoid 404 noise)
      api
        .get(addCacheBuster('/api/revolut/sync-status'))
        .then((res: any) => {
          const raw = res.data?.sync_status;
          if (!raw || typeof raw !== 'object') {
            setRevolutSyncMetadata([]);
            return;
          }
          const rows: any[] = [];
          for (const dataType of Object.keys(raw)) {
            const byAccount = (raw as any)[dataType];
            if (!byAccount || typeof byAccount !== 'object') continue;
            for (const accountKey of Object.keys(byAccount)) {
              const rec = (byAccount as any)[accountKey];
              if (rec && typeof rec === 'object' && rec.id != null) {
                rows.push({
                  ...rec,
                  last_sync_at: rec.last_sync_datetime ?? rec.last_sync_at,
                });
              }
            }
          }
          setRevolutSyncMetadata(rows);
        })
        .catch(() => setRevolutSyncMetadata([]));
      
    } catch (err) {
      console.error('Error fetching data:', err);
      setError('Failed to fetch data from database');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAllData();
  }, []);

  const formatFileSize = (bytes: number | null | undefined) => {
    if (bytes == null || Number.isNaN(bytes)) return 'N/A';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  const formatStorageProvider = (provider?: string | null) => {
    switch ((provider || '').trim()) {
      case 'dropbox':
        return 'Dropbox';
      case 'hetzner_s3':
        return 'S3';
      case 'local_fs':
        return 'local storage';
      default:
        return 'remote storage';
    }
  };

  const GBP_TO_EUR = 1.16;
  const formatCurrency = (amount: number, currency: string = 'EUR') => {
    // Handle invalid or empty currency codes
    const validCurrency = currency && currency.trim() !== '' ? currency : 'EUR';
    
    try {
      const base = new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: validCurrency,
        minimumFractionDigits: 2,
      }).format(amount);
      if (validCurrency === 'GBP') {
        const eur = new Intl.NumberFormat('en-IE', { style: 'currency', currency: 'EUR', minimumFractionDigits: 2 }).format(amount * GBP_TO_EUR);
        return `${base} (${eur})`;
      }
      return base;
    } catch (error) {
      // Fallback to EUR if currency is invalid
      return new Intl.NumberFormat('en-IE', {
        style: 'currency',
        currency: 'EUR',
        minimumFractionDigits: 2,
      }).format(amount);
    }
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (dateString == null || dateString === '') return 'N/A';
    const d = new Date(dateString);
    return Number.isNaN(d.getTime()) ? String(dateString) : d.toLocaleDateString('en-IE');
  };

  const transactionLedgerDate = (tx: Transaction) =>
    tx.display_date ?? tx.date ?? tx.completed_at ?? null;

  const deleteAllRevolutTransactions = async () => {
    if (!window.confirm('Are you sure you want to delete ALL Revolut transactions? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingTransactions(true);
      setError(null);

      // Use the admin endpoint to delete all transactions from database
      const response = await api.post('/api/admin/revolut-transactions/clear-all');
      
      console.log('Delete response:', response.data);
      alert(response.data.message || 'Successfully deleted Revolut transactions');

      // Refresh data
      await fetchAllData();

    } catch (err) {
      console.error('Error deleting transactions:', err);
      setError('Failed to delete transactions');
    } finally {
      setDeletingTransactions(false);
    }
  };

  const deleteAllWebhooks = async () => {
    if (!window.confirm('Are you sure you want to delete ALL webhooks from the database? This action cannot be undone.')) {
      return;
    }

    try {
      setDeletingWebhooks(true);
      setError(null);

      // Use the admin endpoint to delete all webhooks
      const response = await api.post('/api/admin/webhooks/clear-all');
      
      console.log('Delete webhooks response:', response.data);
      alert(response.data.message || 'Successfully deleted all webhooks');

      // Refresh data
      await fetchAllData();

    } catch (err: any) {
      console.error('Error deleting webhooks:', err);
      setError(err.response?.data?.error || 'Failed to delete webhooks');
    } finally {
      setDeletingWebhooks(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'completed':
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const handleViewDetails = (entity: any, entityType: string) => {
    setSelectedEntity(entity);
    setSelectedEntityType(entityType);
    setTxCommentsError(null);
    setQvPanelOpen(true);
  };

  React.useEffect(() => {
    if (selectedEntityType !== 'Transaction' || !selectedEntity?.id) {
      setTxCommentInternal('');
      setTxCommentExternal('');
      return;
    }
    const dm = (selectedEntity as any).derived_metadata || {};
    setTxCommentInternal(typeof dm.comment_internal === 'string' ? dm.comment_internal : '');
    setTxCommentExternal(typeof dm.comment_external === 'string' ? dm.comment_external : '');
  }, [selectedEntity, selectedEntityType, qvPanelOpen]);

  const saveTxAppendedComments = async () => {
    if (selectedEntityType !== 'Transaction' || !(selectedEntity as any)?.id) return;
    const txId = (selectedEntity as any).id;
    setTxCommentsSaving(true);
    setTxCommentsError(null);
    try {
      const res = await api.patch(`/api/revolut/transactions/complete/${txId}/appended-comments`, {
        comment_internal: txCommentInternal,
        comment_external: txCommentExternal,
      });
      const dm = res.data?.derived_metadata || {};
      setSelectedEntity((prev: any) =>
        prev && prev.id === txId
          ? {
              ...prev,
              derived_metadata: {
                ...(prev.derived_metadata || {}),
                ...dm,
              },
            }
          : prev
      );
      setTxCommentInternal(typeof dm.comment_internal === 'string' ? dm.comment_internal : '');
      setTxCommentExternal(typeof dm.comment_external === 'string' ? dm.comment_external : '');
      setTransactions((prev) =>
        prev.map((t) =>
          t.id === txId
            ? {
                ...t,
                derived_metadata: {
                  ...((t as any).derived_metadata || {}),
                  ...dm,
                },
              } as Transaction
            : t
        )
      );
    } catch (e: any) {
      const msg = e?.response?.data?.error || e?.message || 'Failed to save comments';
      setTxCommentsError(msg);
    } finally {
      setTxCommentsSaving(false);
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedEntity(null);
    setSelectedEntityType('');
  };

  const handleCloseQvPanel = () => {
    setQvPanelOpen(false);
  };

  const rawJsonValue = React.useMemo(() => {
    if (selectedEntity === undefined) return 'undefined';
    try {
      return JSON.stringify(selectedEntity, null, 2);
    } catch (error) {
      return `Unable to render JSON: ${String(error)}`;
    }
  }, [selectedEntity]);

  const buildMermaidErdFromSchema = React.useCallback((schema: any): string => {
    const tables: Array<{ name: string; columns?: Array<{ name: string }> }> = Array.isArray(schema?.tables)
      ? schema.tables
      : [];
    const tableNames = new Set(tables.map((t) => String(t.name)));
    const edges = new Map<string, { parent: string; child: string; column: string }>();
    const explicitFks: Array<any> = Array.isArray(schema?.foreign_keys) ? schema.foreign_keys : [];

    if (explicitFks.length > 0) {
      for (const fk of explicitFks) {
        const parent = String(fk?.parent_table || '');
        const child = String(fk?.child_table || '');
        if (!parent || !child || !tableNames.has(parent) || !tableNames.has(child) || parent === child) continue;
        const cols = Array.isArray(fk?.child_columns) ? fk.child_columns : [];
        const columnLabel = cols.length > 0 ? cols.join(', ') : 'fk';
        const edgeKey = `${parent}::${child}::${columnLabel}`;
        edges.set(edgeKey, { parent, child, column: columnLabel });
      }
    }

    const inferTargetTable = (columnName: string): string | null => {
      if (!columnName || !columnName.endsWith('_id') || columnName === 'id') return null;
      const rawBase = columnName.slice(0, -3);
      const normalizedBase = rawBase.replace(/_db$/, '');
      const trimmedBase = normalizedBase.replace(/^(manual_|ical_)/, '');
      const candidates = [
        normalizedBase,
        `${normalizedBase}s`,
        `${normalizedBase}es`,
        normalizedBase.endsWith('y') ? `${normalizedBase.slice(0, -1)}ies` : '',
        trimmedBase,
        `${trimmedBase}s`,
        `${trimmedBase}es`,
        trimmedBase.endsWith('y') ? `${trimmedBase.slice(0, -1)}ies` : '',
      ].filter(Boolean);

      for (const candidate of candidates) {
        if (tableNames.has(candidate)) return candidate;
      }
      return null;
    };

    // Fallback inference only when backend doesn't return explicit FK metadata.
    if (edges.size === 0) {
      for (const table of tables) {
        const child = String(table.name);
        const columns = Array.isArray(table.columns) ? table.columns : [];
        for (const col of columns) {
          const colName = String(col?.name || '');
          const parent = inferTargetTable(colName);
          if (!parent || parent === child) continue;
          const edgeKey = `${parent}::${child}::${colName}`;
          edges.set(edgeKey, { parent, child, column: colName });
        }
      }
    }

    const lines: string[] = ['erDiagram'];
    for (const table of Array.from(tableNames).sort()) {
      lines.push(`    ${table} {}`);
    }
    const sortedEdges = Array.from(edges.values()).sort((a, b) =>
      `${a.parent}.${a.child}.${a.column}`.localeCompare(`${b.parent}.${b.child}.${b.column}`)
    );
    for (const edge of sortedEdges) {
      lines.push(`    ${edge.parent} ||--o{ ${edge.child} : "${edge.column}"`);
    }
    return lines.join('\n');
  }, []);

  const generateErdDiagram = React.useCallback(async () => {
    if (!databaseSchema?.tables?.length) {
      setErdError('No schema data available yet. Click Refresh first.');
      setErdSvg('');
      setErdSource('');
      return;
    }

    const source = buildMermaidErdFromSchema(databaseSchema);
    setErdSource(source);
    try {
      const mermaid = (await import('mermaid')).default;
      mermaid.initialize({ startOnLoad: false, securityLevel: 'loose', theme: 'default' });
      const id = `db-overview-erd-${Date.now()}`;
      const rendered = await mermaid.render(id, source);
      setErdSvg(rendered.svg);
      setErdError('');
    } catch (error) {
      console.error('Failed to render Mermaid ERD:', error);
      setErdSvg('');
      setErdError('Failed to render ERD diagram. Mermaid parser rejected generated schema.');
    }
  }, [buildMermaidErdFromSchema, databaseSchema]);

  useEffect(() => {
    if (schemaTab === 2) {
      generateErdDiagram();
    }
  }, [schemaTab, generateErdDiagram]);

  useEffect(() => {
    const viewNames = ['backups', 'tables', 'erd'] as const;
    const url = new URL(window.location.href);
    url.searchParams.set('view', viewNames[schemaTab] ?? 'backups');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
  }, [schemaTab]);

  const renderEntityDetails = () => {
    if (!selectedEntity) return null;

    const formatArrayValue = (key: string, array: any[]): string => {
      if (array.length === 0) return 'None';
      
      // Format based on the type of items in the array
      const firstItem = array[0];
      
      // Handle people array
      if (key === 'people' || (firstItem && firstItem.name && firstItem.relationship !== undefined)) {
        return array.map((person: any) => {
          const parts = [person.name || 'Unnamed'];
          if (person.relationship) parts.push(`(${person.relationship})`);
          if (person.email) parts.push(`- ${person.email}`);
          return parts.join(' ');
        }).join(', ');
      }
      
      // Handle loans array
      if (key === 'loans' || (firstItem && firstItem.lender !== undefined)) {
        return array.map((loan: any) => {
          const parts: string[] = [];
          if (loan.lender) parts.push(loan.lender);
          if (loan.original_amount) parts.push(formatCurrency(loan.original_amount));
          if (loan.loan_name) parts.push(`(${loan.loan_name})`);
          if (loan.property_id) parts.push(`Property ID: ${loan.property_id}`);
          return parts.length > 0 ? parts.join(' | ') : `Loan ID: ${loan.id || 'N/A'}`;
        }).join('; ');
      }
      
      // Handle properties array
      if (key === 'properties' || (firstItem && firstItem.nickname !== undefined && firstItem.address !== undefined)) {
        return array.map((property: any) => {
          const parts: string[] = [];
          if (property.nickname) parts.push(property.nickname);
          if (property.address) parts.push(`(${property.address})`);
          return parts.length > 0 ? parts.join(' ') : `Property ID: ${property.id || 'N/A'}`;
        }).join(', ');
      }
      
      // Handle families array
      if (key === 'families' || (firstItem && firstItem.name && firstItem.description !== undefined)) {
        return array.map((family: any) => family.name || `Family ID: ${family.id || 'N/A'}`).join(', ');
      }
      
      // Handle companies array
      if (key === 'companies' || (firstItem && firstItem.company_name !== undefined)) {
        return array.map((company: any) => {
          const parts: string[] = [];
          if (company.company_name) parts.push(company.company_name);
          if (company.is_active !== undefined) parts.push(company.is_active ? '(Active)' : '(Inactive)');
          return parts.length > 0 ? parts.join(' ') : `Company ID: ${company.id || 'N/A'}`;
        }).join(', ');
      }
      
      // Handle generic objects with name/id
      if (firstItem && (firstItem.name || firstItem.id)) {
        return array.map((item: any) => {
          if (item.name) return item.name;
          if (item.id) return `ID: ${item.id}`;
          return 'Unknown';
        }).join(', ');
      }
      
      // Fallback: show count with first few items
      if (array.length <= 3) {
        return array.map((item, idx) => {
          if (typeof item === 'string' || typeof item === 'number') return String(item);
          if (item && typeof item === 'object') {
            if (item.name) return item.name;
            if (item.id) return `ID: ${item.id}`;
            return JSON.stringify(item).substring(0, 30);
          }
          return String(item);
        }).join(', ');
      }
      
      // For longer arrays, show first few items and count
      const preview = array.slice(0, 3).map((item: any) => {
        if (typeof item === 'string' || typeof item === 'number') return String(item);
        if (item && typeof item === 'object') {
          if (item.name) return item.name;
          if (item.id) return `ID: ${item.id}`;
          return '...';
        }
        return String(item);
      }).join(', ');
      
      return `${preview}... (${array.length} total)`;
    };

    const formatValue = (key: string, value: any): string => {
      if (value === null || value === undefined) return 'N/A';
      if (typeof value === 'boolean') return value ? 'Yes' : 'No';
      if (typeof value === 'number') {
        // Check if it's a currency field
        if (key.includes('amount') || key.includes('balance') || key.includes('valuation') || 
            key.includes('income') || key.includes('payment') || key.includes('payout')) {
          return formatCurrency(value);
        }
        // Check if it's a percentage - ownership is stored as decimal (0.5 = 50%)
        if (key.includes('ownership')) {
          return `${(value * 100).toFixed(1)}%`;
        }
        // Other percentages (rates) might already be in percentage form
        if (key.includes('rate') || key.includes('percent')) {
          return `${value}%`;
        }
        return value.toString();
      }
      if (typeof value === 'string') {
        // Check if it's a date string
        if (value.match(/^\d{4}-\d{2}-\d{2}/)) {
          return formatDate(value);
        }
        // Check if it's a JSON string that should be parsed
        if ((value.startsWith('{') || value.startsWith('[')) && value.length > 1) {
          try {
            const parsed = JSON.parse(value);
            // Recursively format the parsed object
            return formatValue(key, parsed);
          } catch (e) {
            // Not valid JSON, return as string
            return value;
          }
        }
        return value;
      }
      if (typeof value === 'object') {
        if (Array.isArray(value)) {
          if (value.length === 0) return 'None';
          // Format array items based on their content
          return formatArrayValue(key, value);
        }
        // Handle person object specially
        if (key === 'person' && value.name) {
          const personParts = [value.name];
          if (value.email) personParts.push(`(${value.email})`);
          if (value.relationship) personParts.push(`- ${value.relationship}`);
          return personParts.join(' ');
        }
        // Handle nested objects - show key fields for common entity types
        if (value.name || value.id) {
          const parts: string[] = [];
          if (value.name) parts.push(value.name);
          if (value.id && key !== 'id') parts.push(`ID: ${value.id}`);
          if (value.email && !value.name) parts.push(value.email);
          return parts.length > 0 ? parts.join(' | ') : JSON.stringify(value, null, 2);
        }
        // For other objects, show JSON but limit length for readability
        const jsonStr = JSON.stringify(value, null, 2);
        if (jsonStr.length > 200) {
          return jsonStr.substring(0, 200) + '...';
        }
        return jsonStr;
      }
      return String(value);
    };

    const formatKey = (key: string): string => {
      return key
        .replace(/_/g, ' ')
        .replace(/([A-Z])/g, ' $1')
        .replace(/^./, str => str.toUpperCase())
        .trim();
    };

    // Handle case where selectedEntity might be a string or primitive
    let entityObj = selectedEntity;
    if (typeof selectedEntity === 'string' || typeof selectedEntity === 'number' || typeof selectedEntity === 'boolean') {
      // If it's a primitive, wrap it in an object
      entityObj = { value: selectedEntity };
    } else if (!selectedEntity || typeof selectedEntity !== 'object' || Array.isArray(selectedEntity)) {
      // If it's null, undefined, or an array, wrap it
      entityObj = { value: selectedEntity };
    }
    
    const entries = Object.entries(entityObj).filter(([key]) => {
      // Filter out internal React/MUI keys
      return !key.startsWith('_') && key !== '$$typeof';
    });

    // Separate ownership fields from other fields
    const ownershipEntries = entries.filter(([key]) => key.includes('ownership'));
    const regularEntries = entries.filter(([key]) => !key.includes('ownership'));

    const isObjectValue = (key: string, value: any): boolean => {
      return typeof value === 'object' && value !== null && !Array.isArray(value) && 
             !(key === 'person' && value.name); // Don't treat formatted person as object
    };

    return (
      <Box>
        {/* Regular fields */}
        <Grid container spacing={2}>
          {regularEntries.map(([key, value]) => {
            const shouldUseMonospace = isObjectValue(key, value);
            const formattedValue = formatValue(key, value);
            
            return (
              <Grid size={{ xs: 12, sm: 6, md: 4 }} key={key}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                    {formatKey(key)}
                  </Typography>
                  <Typography variant="body2" sx={{ 
                    wordBreak: 'break-word',
                    fontFamily: shouldUseMonospace ? 'monospace' : 'inherit',
                    fontSize: shouldUseMonospace ? '0.75rem' : 'inherit',
                    whiteSpace: shouldUseMonospace ? 'pre-wrap' : 'normal'
                  }}>
                    {formattedValue}
                  </Typography>
                </Box>
              </Grid>
            );
          })}
        </Grid>

        {/* Ownership table - only show if there are ownership fields */}
        {ownershipEntries.length > 0 && (
          <Box sx={{ mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Ownership
            </Typography>
            <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'owner' },
                    { key: 'percentage', pct: 30 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell><strong>Owner</strong></TableCell>
                    <TableCell align="right"><strong>Percentage</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ownershipEntries
                    .sort(([keyA, valueA], [keyB, valueB]) => {
                      // Sort: non-zero values first (descending), then zero values (alphabetically)
                      const valA = (valueA as number) || 0;
                      const valB = (valueB as number) || 0;
                      const isZeroA = valA === 0;
                      const isZeroB = valB === 0;
                      
                      // Non-zero values come first
                      if (isZeroA !== isZeroB) {
                        return isZeroA ? 1 : -1;
                      }
                      
                      // If both are zero or both are non-zero, sort by value (descending for non-zero) or name (alphabetically for zero)
                      if (!isZeroA) {
                        // Both non-zero: sort by value descending, then by name
                        if (valB !== valA) return valB - valA;
                        return keyA.localeCompare(keyB);
                      } else {
                        // Both zero: sort alphabetically by name
                        return keyA.localeCompare(keyB);
                      }
                    })
                    .map(([key, value]) => {
                      const val = (value as number) || 0;
                      const isZero = val === 0 || value === null || value === undefined;
                      
                      return (
                        <TableRow 
                          key={key}
                          sx={{
                            opacity: isZero ? 0.6 : 1,
                            '&:hover': {
                              backgroundColor: 'action.hover'
                            }
                          }}
                        >
                          <TableCell>
                            <Typography 
                              variant="body2" 
                              sx={{ 
                                fontStyle: isZero ? 'italic' : 'normal',
                                color: isZero ? 'text.secondary' : 'text.primary'
                              }}
                            >
                              {formatKey(key)}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Typography 
                              variant="body2" 
                              fontWeight={isZero ? 'normal' : 'medium'}
                              sx={{ color: isZero ? 'text.secondary' : 'text.primary' }}
                            >
                              {formatValue(key, value)}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
      </Box>
    );
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="h6" sx={{ ml: 2 }}>
          Loading database overview...
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1" sx={{ display: 'flex', alignItems: 'center' }}>
          <TrendingUpIcon sx={{ mr: 1 }} />
          Database Overview
        </Typography>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={deleteAllRevolutTransactions}
            disabled={deletingTransactions || loading}
            sx={{ mr: 1 }}
          >
            {deletingTransactions ? 'Deleting...' : 'Delete Revolut Transactions'}
          </Button>
          <Button
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            onClick={deleteAllWebhooks}
            disabled={deletingWebhooks || loading}
            sx={{ mr: 1 }}
          >
            {deletingWebhooks ? 'Deleting...' : 'Delete All Webhooks'}
          </Button>
          <Tooltip title="Refresh all data">
            <IconButton onClick={fetchAllData} color="primary">
              <RefreshIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
        <Tabs
          value={schemaTab}
          onChange={(_, val) => setSchemaTab(val)}
          aria-label="Database overview: backups, tables, or ERD"
        >
          <Tab label="Backups" id="db-overview-tab-0" aria-controls="db-overview-panel-0" />
          <Tab label="Tables" id="db-overview-tab-1" aria-controls="db-overview-panel-1" />
          <Tab label="ERD" id="db-overview-tab-2" aria-controls="db-overview-panel-2" />
        </Tabs>
      </Box>

      {/* Last refresh info */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="body2" color="text.secondary">
          <strong>Last refreshed:</strong> {lastRefresh.toLocaleString()} ({Math.round((Date.now() - lastRefresh.getTime()) / 1000)}s ago)
        </Typography>
      {databaseSchema && schemaTab === 1 && (
          <Typography variant="body2" color="text.secondary">
            <strong>Schema timestamp:</strong> {databaseSchema.timestamp ? new Date(databaseSchema.timestamp).toLocaleString() : 'N/A'} | 
            <strong> DB:</strong> {databaseSchema.database_type || 'unknown'} |
            <strong> Tables:</strong> {databaseSchema.tables?.length || 0} |
            <strong> Rows:</strong> {databaseSchema.summary?.total_rows?.toLocaleString?.() ?? databaseSchema.summary?.total_rows ?? 'N/A'} |
            <strong> FKs:</strong> {databaseSchema.foreign_keys?.length || 0}
          </Typography>
        )}
      </Box>

      {/* Error alert */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {schemaTab === 0 && (
        <Box role="tabpanel" id="db-overview-panel-0" aria-labelledby="db-overview-tab-0" sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            Nightly cron / sync writes SQL dumps locally to{' '}
            <code>{backupMeta?.backup_dir_configured || 'database/DB_backups/nightly_sql_dumps'}</code>
            {backupMeta?.retention_days != null ? ` (local retention: ${backupMeta.retention_days} days)` : ''}
            and uploads copies to {formatStorageProvider(backupMeta?.storage_provider)} when configured. Ephemeral
            local directories can be empty after container restarts — this tab lists both <strong>local</strong> and{' '}
            <strong>remote</strong> files.{' '}
            {backupMeta?.remote_browse_url ? (
              <a href={backupMeta.remote_browse_url} target="_blank" rel="noopener noreferrer">
                Open {formatStorageProvider(backupMeta?.storage_provider)} backups folder
              </a>
            ) : null}
          </Alert>
          {backupMeta?.remote?.error && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Could not list {formatStorageProvider(backupMeta?.storage_provider)} backups: {backupMeta.remote.error}
            </Alert>
          )}
          {backupsError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {backupsError}
            </Alert>
          )}
          {backupsLoading && (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
              <CircularProgress size={18} />
              <Typography variant="body2" color="text.secondary">
                Loading backup files…
              </Typography>
            </Box>
          )}
          {!backupsLoading && !backupsError && backupMeta && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              <strong>Local directory:</strong>{' '}
              <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                {backupMeta.backup_dir}
              </Typography>
              {' · '}
              <strong>{backupMeta.local_file_count ?? 0}</strong> local
              {' · '}
              <strong>{backupMeta.remote_file_count ?? 0}</strong> {formatStorageProvider(backupMeta?.storage_provider)}
              {' · '}
              <strong>{backupMeta.file_count ?? backupFiles.length}</strong> total
              {backupMeta.remote?.folder ? (
                <>
                  {' · '}
                  {formatStorageProvider(backupMeta?.storage_provider)} prefix{' '}
                  <Typography component="span" variant="body2" sx={{ fontFamily: 'monospace' }}>
                    {backupMeta.remote.folder}
                  </Typography>
                </>
              ) : null}
              {backupMeta.listed_at ? ` · listed ${new Date(backupMeta.listed_at).toLocaleString()}` : ''}
            </Typography>
          )}
          {!backupsLoading && (backupMeta?.file_count ?? backupFiles.length) === 0 && !backupsError ? (
            <Alert severity="warning">
              No <code>*.sql</code> backup files found locally or in {formatStorageProvider(backupMeta?.storage_provider)}.
              Run cron sync (backup stage) or fix storage credentials if uploads are failing.
            </Alert>
          ) : (
            !backupsLoading &&
            backupFiles.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={DB_FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'source', pct: 12 },
                      { key: 'file' },
                      { key: 'size', pct: 12 },
                      { key: 'modified', pct: 20 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>
                        <strong>Source</strong>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                          <BackupIcon fontSize="small" color="action" />
                          <strong>File</strong>
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <strong>Size</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Modified</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {backupFiles.map((file: any) => (
                      <TableRow key={`${file.source || 'local'}:${file.path_display || file.name}`}>
                        <TableCell>
                          <Chip
                            label={file.source === 'local' ? 'Local' : formatStorageProvider(file.provider)}
                            size="small"
                            color={file.source === 'local' ? 'default' : 'primary'}
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Typography
                            variant="body2"
                            sx={{ ...fitTruncateSx, fontFamily: 'monospace' }}
                            title={file.path_display || file.name}
                          >
                            {file.path_display || file.name}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">{formatFileSize(file.size_bytes)}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {file.modified_at ? new Date(file.modified_at).toLocaleString() : 'N/A'}
                          </Typography>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )
          )}
          <Box sx={{ mt: 2 }}>
            <Button size="small" variant="outlined" startIcon={<RefreshIcon />} onClick={fetchBackups} disabled={backupsLoading}>
              Refresh backup list
            </Button>
          </Box>
        </Box>
      )}

      {schemaTab === 1 && (
        <Box role="tabpanel" id="db-overview-panel-1" aria-labelledby="db-overview-tab-1" sx={{ mb: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">
                Database schema
                {databaseSchema?.tables?.length ? ` (${databaseSchema.tables.length} tables)` : ''}
              </Typography>
            </AccordionSummary>
            <AccordionDetails>
              {databaseSchemaLoading && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                  <CircularProgress size={18} />
                  <Typography variant="body2" color="text.secondary">
                    Loading schema metadata...
                  </Typography>
                </Box>
              )}
              {databaseSchemaError && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  {databaseSchemaError}
                </Alert>
              )}
              {!databaseSchema?.tables?.length ? (
                <Alert severity="info">
                  Schema table metadata is not available yet. Click the refresh icon at the top-right to retry.
                </Alert>
              ) : (
                <TableContainer component={Paper} variant="outlined" sx={FIT_TABLE_CONTAINER_SX}>
                  <Table size="small" sx={DB_FIT_TABLE_SX}>
                    <FitColgroup
                      cols={[
                        { key: 'table_name', pct: 26 },
                        { key: 'rows', pct: 10 },
                        { key: 'columns' },
                      ]}
                    />
                    <TableHead>
                      <TableRow>
                        <TableCell><strong>Table Name</strong></TableCell>
                        <TableCell align="right"><strong>Rows</strong></TableCell>
                        <TableCell><strong>Columns</strong></TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {databaseSchema.tables.map((table: any) => (
                        <TableRow key={table.name}>
                          <TableCell>
                            <Typography variant="body2" sx={{ fontFamily: 'monospace', fontWeight: 'medium' }}>
                              {table.name}
                            </Typography>
                          </TableCell>
                          <TableCell align="right">
                            <Chip
                              label={table.row_count?.toLocaleString() || '0'}
                              size="small"
                              color={table.row_count > 0 ? 'primary' : 'default'}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {table.columns?.slice(0, 5).map((col: any) => (
                                <Chip
                                  key={col.name}
                                  label={`${col.name} (${col.type})`}
                                  size="small"
                                  variant="outlined"
                                />
                              ))}
                              {table.columns?.length > 5 && (
                                <Chip
                                  label={`+${table.columns.length - 5} more`}
                                  size="small"
                                  variant="outlined"
                                  color="secondary"
                                />
                              )}
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </AccordionDetails>
          </Accordion>

          <Accordion defaultExpanded>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="h6">Table row data</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        
        {/* Properties Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Properties ({properties.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'nickname', pct: 12 },
                    { key: 'address' },
                    { key: 'valuation', pct: 11 },
                    { key: 'rental_income', pct: 11 },
                    { key: 'omar_pct', pct: 7 },
                    { key: 'heidi_pct', pct: 7 },
                    { key: 'loans', pct: 6 },
                    { key: 'ical_urls', pct: 12 },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Nickname</TableCell>
                    <TableCell>Address</TableCell>
                    <TableCell align="right">Valuation</TableCell>
                    <TableCell align="right">Rental Income</TableCell>
                    <TableCell align="right">Omar %</TableCell>
                    <TableCell align="right">Heidi %</TableCell>
                    <TableCell align="right">Loans</TableCell>
                    <TableCell>iCal URLs</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {properties.map((property) => (
                    <TableRow key={property.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold" sx={fitTruncateSx} title={property.nickname}>
                          {property.nickname}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2" sx={fitTruncateSx} title={property.address}>
                          {property.address}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(property.valuation)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(property.rental_income_yearly)}/year
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(property.omar_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(property.heidi_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {property.loans?.length || 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          {property.airbnb_ical_url && (
                            <Chip label="Airbnb" size="small" color="primary" />
                          )}
                          {property.vrbo_ical_url && (
                            <Chip label="VRBO" size="small" color="secondary" />
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(property, 'Property')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Bookings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Bookings ({bookings.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'property', pct: 14 },
                    { key: 'platform', pct: 10 },
                    { key: 'check_in', pct: 11 },
                    { key: 'check_out', pct: 11 },
                    { key: 'nights', pct: 7 },
                    { key: 'guests', pct: 7 },
                    { key: 'status', pct: 11 },
                    { key: 'source' },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Platform</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Check-out</TableCell>
                    <TableCell align="right">Nights</TableCell>
                    <TableCell align="right">Guests</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {booking.property_nickname}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={booking.platform} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_in_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_out_date)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {booking.nights}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {booking.adults_count + booking.children_count}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={booking.status} 
                          size="small" 
                          color={getStatusColor(booking.status) as any}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.source}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(booking, 'Booking')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Loans Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <LoanIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Loans ({loans.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'loan_name' },
                    { key: 'lender', pct: 11 },
                    { key: 'property', pct: 11 },
                    { key: 'original_amount', pct: 11 },
                    { key: 'current_balance', pct: 11 },
                    { key: 'interest_rate', pct: 9 },
                    { key: 'monthly_payment', pct: 11 },
                    { key: 'type', pct: 9 },
                    { key: 'actions', pct: 8 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Loan Name</TableCell>
                    <TableCell>Lender</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell align="right">Original Amount</TableCell>
                    <TableCell align="right">Current Balance</TableCell>
                    <TableCell align="right">Interest Rate</TableCell>
                    <TableCell align="right">Monthly Payment</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {loans.map((loan) => (
                    <TableRow key={loan.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {loan.loan_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {loan.lender}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {loan.property?.nickname || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(loan.original_amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(loan.current_balance)}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {loan.interest_rate}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(loan.monthly_payment)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={loan.loan_type} size="small" />
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(loan, 'Loan')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Companies Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Companies ({companies.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'name' },
                    { key: 'type', pct: 10 },
                    { key: 'country', pct: 9 },
                    { key: 'omar_pct', pct: 7 },
                    { key: 'heidi_pct', pct: 7 },
                    { key: 'sean_pct', pct: 7 },
                    { key: 'dwayne_pct', pct: 8 },
                    { key: 'lena_pct', pct: 7 },
                    { key: 'status', pct: 9 },
                    { key: 'actions', pct: 8 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Country</TableCell>
                    <TableCell align="right">Omar %</TableCell>
                    <TableCell align="right">Heidi %</TableCell>
                    <TableCell align="right">Sean %</TableCell>
                    <TableCell align="right">Dwayne %</TableCell>
                    <TableCell align="right">Lena %</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {companies.map((company) => (
                    <TableRow key={company.id}>
                      <TableCell>
                        <Typography variant="subtitle2" fontWeight="bold">
                          {company.name}
                        </Typography>
                        {company.nickname && (
                          <Typography variant="body2" color="text.secondary">
                            ({company.nickname})
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.company_type}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {company.country || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(company.omar_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(company.heidi_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(company.sean_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(company.dwayne_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {(company.lena_ownership * 100).toFixed(1)}%
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={company.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={company.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(company, 'Company')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* People Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">People ({people.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'name' },
                    { key: 'relationship', pct: 11 },
                    { key: 'rr_director', pct: 8 },
                    { key: 'secodwom_director', pct: 10 },
                    { key: 'omhe_director', pct: 9 },
                    { key: 'deceased', pct: 8 },
                    { key: 'family_id', pct: 8 },
                    { key: 'created', pct: 12 },
                    { key: 'actions', pct: 8 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Relationship</TableCell>
                    <TableCell>RR Director</TableCell>
                    <TableCell>Secodwom Director</TableCell>
                    <TableCell>OmHe Director</TableCell>
                    <TableCell>Deceased</TableCell>
                    <TableCell>Family ID</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {people.map((person) => (
                    <TableRow key={person.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {person.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {person.relationship || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={person.is_reinvented_recruitment_director ? 'Yes' : 'No'} 
                          size="small" 
                          color={person.is_reinvented_recruitment_director ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={person.is_secodwom_uk_ltd_director ? 'Yes' : 'No'} 
                          size="small" 
                          color={person.is_secodwom_uk_ltd_director ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={person.is_omhe_props_ltd_director ? 'Yes' : 'No'} 
                          size="small" 
                          color={person.is_omhe_props_ltd_director ? 'primary' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={person.is_deceased ? 'Yes' : 'No'} 
                          size="small" 
                          color={person.is_deceased ? 'error' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {person.family_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(person.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(person, 'Person')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Families Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <FamilyIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Families ({families.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'name', pct: 18 },
                    { key: 'description' },
                    { key: 'people_count', pct: 12 },
                    { key: 'created', pct: 16 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>People Count</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {families.map((family) => (
                    <TableRow key={family.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {family.name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {family.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {family.people ? family.people.length : 0}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(family.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(family, 'Family')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Incomes Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <IncomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Incomes ({incomes.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'source' },
                    { key: 'person', pct: 14 },
                    { key: 'monthly', pct: 12 },
                    { key: 'yearly', pct: 12 },
                    { key: 'status', pct: 11 },
                    { key: 'created', pct: 13 },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Source</TableCell>
                    <TableCell>Person</TableCell>
                    <TableCell align="right">Monthly</TableCell>
                    <TableCell align="right">Yearly</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {incomes.map((income) => (
                    <TableRow key={income.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {income.source}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {income.person ? income.person.name : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(income.amount_monthly, 'EUR')}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2">
                          {formatCurrency(income.amount_yearly, 'EUR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip 
                          label={income.is_active ? 'Active' : 'Inactive'} 
                          size="small" 
                          color={income.is_active ? 'success' : 'default'}
                        />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(income.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(income, 'Income')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Accounts Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <AccountIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Bank Accounts ({accounts.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'account_name' },
                    { key: 'bank', pct: 14 },
                    { key: 'account_number', pct: 18 },
                    { key: 'balance', pct: 13 },
                    { key: 'created', pct: 14 },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Account Name</TableCell>
                    <TableCell>Bank</TableCell>
                    <TableCell>Account Number</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {accounts.map((account) => (
                    <TableRow key={account.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {account.account_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.bank_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {account.account_number || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(account.current_balance, account.account_type === 'business' ? 'EUR' : 'EUR')}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(account.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(account, 'Account')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Balances Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BalancesIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Account Balances ({balances.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 13 },
                    { key: 'account' },
                    { key: 'loan', pct: 16 },
                    { key: 'balance', pct: 12 },
                    { key: 'currency', pct: 9 },
                    { key: 'created', pct: 13 },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Account</TableCell>
                    <TableCell>Loan</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell>Created</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {balances.map((balance) => (
                    <TableRow key={balance.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(balance.date_entered)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {balance.account_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {balance.loan_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(balance.balance, balance.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {balance.currency || 'EUR'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(balance.created_at)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(balance, 'Balance')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Transactions Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TransactionIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Transactions ({transactions.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            {transactions.length === 0 ? (
              <Alert severity="warning">
                <Typography variant="h6">No Transactions Found</Typography>
                <Typography>
                  The Revolut sync is not working properly. This is the issue we've been debugging.
                  The API is returning transaction data, but it's not being saved to the database.
                </Typography>
              </Alert>
            ) : (
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={DB_FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'id', pct: 6 },
                      { key: 'date', pct: 9 },
                      { key: 'merchant', pct: 12 },
                      { key: 'bank_account_id', pct: 8 },
                      { key: 'description' },
                      { key: 'amount', pct: 9 },
                      { key: 'balance', pct: 9 },
                      { key: 'category', pct: 10 },
                      { key: 'source', pct: 9 },
                      { key: 'actions', pct: 8 },
                    ]}
                  />
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Date</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell>Bank account id</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="right">Balance</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Source</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                  <TableBody>
                    {transactions.map((transaction) => (
                      <TableRow key={transaction.id}>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {transaction.id}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(transactionLedgerDate(transaction))}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {effectiveMerchantName(transaction) || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" sx={{ fontFamily: 'monospace' }}>
                            {transaction.bank_account_id ?? 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.description || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography 
                            variant="body2" 
                            color={transaction.amount >= 0 ? 'success.main' : 'error.main'}
                            fontWeight="bold"
                          >
                            {formatCurrency(transaction.amount, transaction.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2">
                            {transaction.balance ? formatCurrency(transaction.balance, transaction.currency) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {transaction.merchant_category || transaction.category || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={transaction.revolut_id ? 'API Sync' : 'CSV Import'} 
                            size="small" 
                            color={transaction.revolut_id ? 'primary' : 'secondary'}
                          />
                        </TableCell>
                        <TableCell align="center" className="fit-table-no-clip">
                          <Tooltip title="View all details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(transaction, 'Transaction')}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </AccordionDetails>
        </Accordion>

        {/* Users Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Users ({users.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'email' },
                    { key: 'name', pct: 16 },
                    { key: 'role', pct: 10 },
                    { key: 'active', pct: 8 },
                    { key: 'last_login', pct: 16 },
                    { key: 'actions', pct: 9 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Email</TableCell>
                    <TableCell>Name</TableCell>
                    <TableCell>Role</TableCell>
                    <TableCell>Active</TableCell>
                    <TableCell>Last Login</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((user) => (
                    <TableRow key={user.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {user.email}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.first_name} {user.last_name}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={user.role} size="small" color={user.role === 'admin' || user.role === 'owner' ? 'primary' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Chip label={user.is_active ? 'Active' : 'Inactive'} size="small" color={user.is_active ? 'success' : 'default'} />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {user.last_login ? formatDate(user.last_login) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(user, 'User')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Host Expenses Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Host Expenses ({hostExpenses.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 13 },
                    { key: 'property', pct: 18 },
                    { key: 'receipt_from' },
                    { key: 'amount', pct: 13 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Receipt From</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {hostExpenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(expense.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {expense.property_nickname || `Property ID: ${expense.property_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {expense.receipt_from}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(expense.amount)}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(expense, 'Host Expense')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Regular Transactions Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <TransactionIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Regular Transactions ({regularTransactions.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 13 },
                    { key: 'description' },
                    { key: 'amount', pct: 13 },
                    { key: 'currency', pct: 10 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {regularTransactions.map((tx) => (
                    <TableRow key={tx.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(tx.date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {tx.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(tx.amount, tx.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {tx.currency || 'EUR'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(tx, 'Regular Transaction')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Revolut Expenses Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Revolut Expenses ({revolutExpenses.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'date', pct: 13 },
                    { key: 'merchant' },
                    { key: 'amount', pct: 13 },
                    { key: 'currency', pct: 10 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell>
                    <TableCell>Merchant</TableCell>
                    <TableCell align="right">Amount</TableCell>
                    <TableCell>Currency</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revolutExpenses.map((expense) => (
                    <TableRow key={expense.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {expense.completed_at ? formatDate(expense.completed_at) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {expense.merchant_name || expense.description || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="right">
                        <Typography variant="body2" fontWeight="bold">
                          {formatCurrency(expense.amount ? expense.amount / 100 : 0, expense.currency)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {expense.currency || 'EUR'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(expense, 'Revolut Expense')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Revolut Counterparties Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <BusinessIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Revolut Counterparties ({revolutCounterparties.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'name' },
                    { key: 'type', pct: 18 },
                    { key: 'account_type', pct: 18 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Account Type</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revolutCounterparties.map((counterparty) => (
                    <TableRow key={counterparty.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {counterparty.name || 'Unnamed'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={counterparty.profile_type || 'N/A'} size="small" />
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {counterparty.account_type || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(counterparty, 'Revolut Counterparty')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Webhooks Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <ReceiptIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Webhooks ({webhooks.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'event_type' },
                    { key: 'timestamp', pct: 22 },
                    { key: 'status', pct: 14 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Event Type</TableCell>
                    <TableCell>Timestamp</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {webhooks.map((webhook) => (
                    <TableRow key={webhook.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {webhook.event_type || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {webhook.timestamp ? formatDate(webhook.timestamp) : 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={webhook.status || 'N/A'} size="small" />
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(webhook, 'Webhook')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* User Entity Permissions Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <PersonIcon sx={{ mr: 1 }} />
              <Typography variant="h6">User Entity Permissions ({userEntityPermissions.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'user_id', pct: 12 },
                    { key: 'entity_type', pct: 20 },
                    { key: 'entity_id', pct: 12 },
                    { key: 'permission' },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Entity Type</TableCell>
                    <TableCell>Entity ID</TableCell>
                    <TableCell>Permission</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {userEntityPermissions.map((permission) => (
                    <TableRow key={permission.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {permission.user_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {permission.entity_type || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {permission.entity_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip label={permission.permission || 'N/A'} size="small" />
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(permission, 'User Entity Permission')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Revolut Sync Metadata Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <RefreshIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Revolut Sync Metadata ({revolutSyncMetadata.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'user_id', pct: 12 },
                    { key: 'account_source' },
                    { key: 'last_sync', pct: 22 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>User ID</TableCell>
                    <TableCell>Account Source</TableCell>
                    <TableCell>Last Sync</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {revolutSyncMetadata.map((metadata) => (
                    <TableRow key={metadata.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {metadata.user_id}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {metadata.account_source || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {metadata.last_sync_at ? formatDate(metadata.last_sync_at) : 'Never'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(metadata, 'Revolut Sync Metadata')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Airbnb Listings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Airbnb Listings ({airbnbListings.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'title' },
                    { key: 'property', pct: 22 },
                    { key: 'listing_id', pct: 20 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Listing ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {airbnbListings.map((listing) => (
                    <TableRow key={listing.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {listing.title || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {listing.property_nickname || `Property ID: ${listing.property_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {listing.listing_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(listing, 'Airbnb Listing')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* Airbnb Bookings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1 }} />
              <Typography variant="h6">Airbnb Bookings ({airbnbBookings.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'property' },
                    { key: 'check_in', pct: 16 },
                    { key: 'check_out', pct: 16 },
                    { key: 'guest', pct: 22 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Check-out</TableCell>
                    <TableCell>Guest</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {airbnbBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.listing_title || `Listing ID: ${booking.listing_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_in_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_out_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.guest_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(booking, 'Airbnb Booking')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* VRBO Listings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <HomeIcon sx={{ mr: 1 }} />
              <Typography variant="h6">VRBO Listings ({vrboListings.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'title' },
                    { key: 'property', pct: 22 },
                    { key: 'listing_id', pct: 20 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Title</TableCell>
                    <TableCell>Property</TableCell>
                    <TableCell>Listing ID</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vrboListings.map((listing) => (
                    <TableRow key={listing.id}>
                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {listing.title || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {listing.property_nickname || `Property ID: ${listing.property_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {listing.listing_id || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(listing, 'VRBO Listing')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* VRBO Bookings Section */}
        <Accordion>
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <CalendarIcon sx={{ mr: 1 }} />
              <Typography variant="h6">VRBO Bookings ({vrboBookings.length})</Typography>
            </Box>
          </AccordionSummary>
          <AccordionDetails>
            <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
              <Table size="small" sx={DB_FIT_TABLE_SX}>
                <FitColgroup
                  cols={[
                    { key: 'property' },
                    { key: 'check_in', pct: 16 },
                    { key: 'check_out', pct: 16 },
                    { key: 'guest', pct: 22 },
                    { key: 'actions', pct: 10 },
                  ]}
                />
                <TableHead>
                  <TableRow>
                    <TableCell>Property</TableCell>
                    <TableCell>Check-in</TableCell>
                    <TableCell>Check-out</TableCell>
                    <TableCell>Guest</TableCell>
                    <TableCell align="center">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {vrboBookings.map((booking) => (
                    <TableRow key={booking.id}>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.listing_title || `Listing ID: ${booking.listing_id}`}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_in_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(booking.check_out_date)}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography variant="body2">
                          {booking.guest_name || 'N/A'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center" className="fit-table-no-clip">
                        <Tooltip title="View all details">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(booking, 'VRBO Booking')}
                          >
                            <VisibilityIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </AccordionDetails>
        </Accordion>

        {/* CSV Reference Transactions Section */}
        {csvReferenceData && csvReferenceData.transactions && (
          <Accordion>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                <TransactionIcon sx={{ mr: 1 }} />
                <Typography variant="h6">CSV Reference Transactions ({csvReferenceData.transactions.length || 0})</Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <TableContainer component={Paper} sx={FIT_TABLE_CONTAINER_SX}>
                <Table size="small" sx={DB_FIT_TABLE_SX}>
                  <FitColgroup
                    cols={[
                      { key: 'revolut_id', pct: 20 },
                      { key: 'date', pct: 13 },
                      { key: 'description' },
                      { key: 'amount', pct: 13 },
                      { key: 'actions', pct: 10 },
                    ]}
                  />
                  <TableHead>
                    <TableRow>
                      <TableCell>Revolut ID</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Description</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell align="center">Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(csvReferenceData.transactions || []).slice(0, 100).map((tx: any) => (
                      <TableRow key={tx.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {tx.revolut_id || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {tx.created_at_revolut ? formatDate(tx.created_at_revolut) : 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {tx.description || 'N/A'}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography variant="body2" fontWeight="bold">
                            {formatCurrency(tx.amount ? tx.amount / 100 : 0, tx.currency)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" className="fit-table-no-clip">
                          <Tooltip title="View all details">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleViewDetails(tx, 'CSV Reference Transaction')}
                            >
                              <VisibilityIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </AccordionDetails>
          </Accordion>
        )}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      {schemaTab === 2 && (
        <Box role="tabpanel" id="db-overview-panel-2" aria-labelledby="db-overview-tab-2" sx={{ mb: 3 }}>
          <Alert severity="info" sx={{ mb: 2 }}>
            ERD is generated from live <code>GET /api/admin/database-schema</code> metadata; FKs use column names
            and explicit database FKs when available.
          </Alert>
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1 }}>
            <Button size="small" variant="outlined" onClick={generateErdDiagram}>
              Regenerate ERD
            </Button>
          </Box>
          {erdError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {erdError}
            </Alert>
          )}
          {!erdError && !databaseSchema?.tables?.length && (
            <Alert severity="info" sx={{ mb: 2 }}>
              No schema payload available, so ERD cannot be generated yet.
            </Alert>
          )}
          {!erdError && erdSvg && (
            <Paper variant="outlined" sx={{ p: 1, overflow: 'auto', maxHeight: 680 }}>
              <Box
                sx={{ minWidth: 900 }}
                dangerouslySetInnerHTML={{ __html: erdSvg }}
              />
            </Paper>
          )}
          <Accordion sx={{ mt: 2 }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />}>
              <Typography variant="body2">View generated Mermaid source</Typography>
            </AccordionSummary>
            <AccordionDetails>
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 2,
                  borderRadius: 1,
                  bgcolor: 'grey.100',
                  border: '1px solid',
                  borderColor: 'divider',
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
                  fontSize: '0.75rem',
                  lineHeight: 1.4,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: 260,
                  overflow: 'auto',
                }}
              >
                {erdSource || 'No ERD source generated yet.'}
              </Box>
            </AccordionDetails>
          </Accordion>
        </Box>
      )}

      <Drawer
        anchor="right"
        open={qvPanelOpen}
        onClose={handleCloseQvPanel}
        PaperProps={{
          sx: {
            width: { xs: '100%', sm: 520, md: 620 },
            maxWidth: '100vw',
          },
        }}
      >
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="h6">QV Raw JSON</Typography>
            <Typography variant="body2" color="text.secondary">
              {selectedEntityType ? `${selectedEntityType} row` : 'Selected row'}
            </Typography>
          </Box>
          <IconButton
            edge="end"
            color="inherit"
            onClick={handleCloseQvPanel}
            aria-label="close quickview panel"
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider />
        {selectedEntityType === 'Transaction' && selectedEntity?.id != null && (
          <Box sx={{ px: 2, pt: 2, pb: 1 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ fontWeight: 600 }}>
              Transaction comments (tx_appended_data)
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
              Internal: private notes (do not share with accountant). External: safe for accountant-facing exports.
            </Typography>
            <TextField
              label="Comment (internal)"
              value={txCommentInternal}
              onChange={(e) => setTxCommentInternal(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={{ mb: 1.5 }}
              disabled={txCommentsSaving}
              placeholder="Private notes…"
            />
            <TextField
              label="Comment (external)"
              value={txCommentExternal}
              onChange={(e) => setTxCommentExternal(e.target.value)}
              fullWidth
              multiline
              minRows={2}
              size="small"
              sx={{ mb: 1.5 }}
              disabled={txCommentsSaving}
              placeholder="Notes to share with accountant…"
            />
            {txCommentsError && (
              <Alert severity="error" sx={{ mb: 1 }}>
                {txCommentsError}
              </Alert>
            )}
            <Button variant="contained" size="small" onClick={saveTxAppendedComments} disabled={txCommentsSaving}>
              {txCommentsSaving ? 'Saving…' : 'Save comments'}
            </Button>
          </Box>
        )}
        <Divider />
        <Box sx={{ p: 2, overflow: 'auto', flex: 1 }}>
          <Box
            component="pre"
            sx={{
              m: 0,
              p: 2,
              borderRadius: 1,
              bgcolor: 'grey.100',
              border: '1px solid',
              borderColor: 'divider',
              fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace',
              fontSize: '0.75rem',
              lineHeight: 1.45,
              whiteSpace: 'pre-wrap',
              wordBreak: 'break-word',
            }}
          >
            {rawJsonValue}
          </Box>
        </Box>
      </Drawer>
    </Box>
  );
};

export default DatabaseOverview;
